import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { ValueType } from 'react-select';
import moment from 'moment';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { getAllAreas } from '../../../shared/services/data/lookups.data';
import { trackEvent, trackPage } from '../../../shared/services/utils/tracking/tracking.service';
import { AreaUiModel } from '../../../shared/ui-models/area.ui-model';
import { PropertyUiModel } from '../../../shared/ui-models/property.ui-model';
import { UnitTypeUiModel } from '../../../shared/ui-models/unit-type.ui-model';
import {
  getAllProperties,
  getPropertyUnitTypes
} from '../../../PropertyModule/shared/services/data.service';
import {
  UnitHistoryBlockedDateModel,
  UnitHistoryListingEventModel,
  UnitHistoryModel,
  UnitHistoryRequestModel
} from '../../../shared/models/unit-history.model';
import { getUnitHistory } from '../../unit-life-cycle.service';

type UnitPublishStatus = 'Published' | 'Unpublished';
type LifeCycleEventType = 'success' | 'warning' | 'error';
type SelectOption = { value: string; label: string };

interface UnitLifeCycleItem {
  id: string;
  name: string;
  area: string;
  project: string;
  floor?: string;
  status: UnitPublishStatus;
  systemAddDate?: string;
  systemAddUser?: string;
  publishDate?: string;
  publishUser?: string;
  lastUnpublishDate?: string;
  lastUnpublishUser?: string;
  blockFrom?: string;
  blockTo?: string;
  blockUser?: string;
  reservations?: ReservationItem[];
  timeline: LifeCycleEvent[];
}

interface ReservationItem {
  id: string;
  dateRange: string;
  status: 'Confirmed' | 'Pending' | 'Cancelled';
}

interface LifeCycleEvent {
  id: string;
  title: string;
  date: string;
  description: string;
  type: LifeCycleEventType;
  timestamp: number;
}

const MODULE_NAME = 'unit-life-cycle-list';

const formatDateTime = (date: string | number) => moment(date).format('DD MMM YYYY, hh:mm A');
const formatDate = (date: number) => moment(date).format('DD MMM YYYY');

const getListingEventTitle = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created':
      return 'Unit added';
    case 'publish':
      return 'Unit published';
    case 'unpublish':
      return 'Unit unpublished';
    default:
      return action;
  }
};

const mapListingEvent = (event: UnitHistoryListingEventModel): LifeCycleEvent => {
  const actorLabel = event.action.toLowerCase() === 'created' ? 'Added by' : 'Changed by';

  return {
    id: `listing-${event.action}-${event.actionDate}`,
    title: getListingEventTitle(event.action),
    date: formatDateTime(event.actionDate),
    description: `${actorLabel} ${event.changedByUsername || 'unknown user'}`,
    type: event.isListed ? 'success' : 'warning',
    timestamp: moment(event.actionDate).valueOf()
  };
};

const mapBlockedDateEvents = (blockedDate: UnitHistoryBlockedDateModel): LifeCycleEvent[] => {
  const dateRange = `${formatDate(blockedDate.fromDate)} – ${formatDate(blockedDate.toDate)}`;
  const blockDescription = blockedDate.description ? `${blockedDate.description}` : undefined;
  const createdEvent: LifeCycleEvent = {
    id: `block-created-${blockedDate.id}`,
    title: 'Block date created',
    date: formatDateTime(blockedDate.createdAt),
    description: [
      dateRange,
      blockDescription,
      `Created by ${blockedDate.createdByUsername || 'unknown user'}`
    ]
      .filter(Boolean)
      .join('\n'),
    type: 'warning',
    timestamp: moment(blockedDate.createdAt).valueOf()
  };

  if (!blockedDate.isCancelled) {
    return [createdEvent];
  }

  const modifiedByUsername =
    blockedDate.modifiedByUsername || blockedDate.cancelledByUsername || 'unknown user';

  return [
    createdEvent,
    {
      id: `block-cancelled-${blockedDate.id}`,
      title: 'Block date cancelled',
      date: formatDateTime(blockedDate.lastUpdateDate),
      description: [dateRange, blockDescription, `Cancelled by ${modifiedByUsername}`]
        .filter(Boolean)
        .join('\n'),
      type: 'warning',
      timestamp: moment(blockedDate.lastUpdateDate).valueOf()
    }
  ];
};

const findLatestListingEvent = (listingHistory: UnitHistoryListingEventModel[], action: string) =>
  [...listingHistory]
    .reverse()
    .find((event) => event.action.toLowerCase() === action.toLowerCase());

const mapUnitHistory = (
  history: UnitHistoryModel,
  area: string,
  project: string
): UnitLifeCycleItem => {
  const listingHistory = [...(history.listingHistory || [])].sort(
    (first, second) => moment(first.actionDate).valueOf() - moment(second.actionDate).valueOf()
  );
  const blockedDates = history.blockedDates || [];
  const latestListingEvent = listingHistory[listingHistory.length - 1];
  const createdEvent = findLatestListingEvent(listingHistory, 'created');
  const publishEvent = findLatestListingEvent(listingHistory, 'publish');
  const unpublishEvent = findLatestListingEvent(listingHistory, 'unpublish');
  const latestActiveBlock = [...blockedDates]
    .filter((blockedDate) => !blockedDate.isCancelled)
    .sort(
      (first, second) => moment(second.createdAt).valueOf() - moment(first.createdAt).valueOf()
    )[0];
  const timeline = [
    ...listingHistory.map(mapListingEvent),
    ...blockedDates.reduce<LifeCycleEvent[]>(
      (events, blockedDate) => [...events, ...mapBlockedDateEvents(blockedDate)],
      []
    )
  ].sort((first, second) => first.timestamp - second.timestamp);
  const containsOnlyCreationEvent =
    timeline.length === 1 &&
    listingHistory.length === 1 &&
    listingHistory[0].action.toLowerCase() === 'created';
  const fallbackTerminationEvent: LifeCycleEvent = {
    id: `terminated-fallback-${history.shortName}`,
    title: 'Terminated',
    date: 'Unknown date',
    description: 'Terminated by unknown person',
    type: 'error',
    timestamp: Number.MAX_SAFE_INTEGER
  };
  const normalizedTimeline = containsOnlyCreationEvent
    ? [...timeline, fallbackTerminationEvent]
    : timeline;

  return {
    id: history.shortName,
    name: history.roomName,
    area,
    project,
    status: latestListingEvent?.isListed ? 'Published' : 'Unpublished',
    systemAddDate: createdEvent ? formatDateTime(createdEvent.actionDate) : undefined,
    systemAddUser: createdEvent?.changedByUsername,
    publishDate: publishEvent ? formatDateTime(publishEvent.actionDate) : undefined,
    publishUser: publishEvent?.changedByUsername,
    lastUnpublishDate: unpublishEvent ? formatDateTime(unpublishEvent.actionDate) : undefined,
    lastUnpublishUser: unpublishEvent?.changedByUsername,
    blockFrom: latestActiveBlock ? formatDate(latestActiveBlock.fromDate) : undefined,
    blockTo: latestActiveBlock ? formatDate(latestActiveBlock.toDate) : undefined,
    blockUser: latestActiveBlock?.createdByUsername,
    timeline: normalizedTimeline
  };
};

const timelineEventClassName: Record<LifeCycleEventType, string> = {
  success: 'bg-[#198357]',
  warning: 'bg-[#FF5C3D]',
  error: 'bg-[#F10000]'
};

const statusClassName: Record<UnitPublishStatus, string> = {
  Published: 'bg-[#E7F2EE] text-[#0E744A]',
  Unpublished: 'bg-[#F3F4F5] text-[#55585F]'
};

const reservationStatusClassName: Record<ReservationItem['status'], string> = {
  Confirmed: 'text-[#0E744A]',
  Pending: 'text-[#0E744A]',
  Cancelled: 'text-[#F10000]'
};

const UnitLifeCycleListContainer: React.FC = () => {
  const [units, setUnits] = useState<UnitLifeCycleItem[]>([]);
  const [areasList, setAreasList] = useState<AreaUiModel[]>([]);
  const [propertiesList, setPropertiesList] = useState<PropertyUiModel[]>([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [unitShortNames, setUnitShortNames] = useState<string[]>([]);
  const [selectedUnitShortName, setSelectedUnitShortName] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState<string>();
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [filtersError, setFiltersError] = useState<string>();
  const [unitsError, setUnitsError] = useState<string>();
  const [historyError, setHistoryError] = useState<string>();
  const unitsRequestIdRef = useRef(0);

  useEffect(() => {
    trackPage(MODULE_NAME);
  }, []);

  useEffect(() => {
    let ignore = false;

    setIsLoadingFilters(true);
    setFiltersError(undefined);

    Promise.all([getAllAreas(MODULE_NAME), getAllProperties()])
      .then(
        ([areasResponse, propertiesResponse]: [
          AreaUiModel[] | ErrorInfo,
          PropertyUiModel[] | ErrorInfo
        ]) => {
          if (ignore) return;

          if (isErrorInfo(areasResponse) || isErrorInfo(propertiesResponse)) {
            setFiltersError('Unable to load area and project options. Please try again.');
            setAreasList([]);
            setPropertiesList([]);
            return;
          }

          setAreasList(areasResponse);
          setPropertiesList(propertiesResponse);
          setSelectedArea(areasResponse[0]?.name || '');
        }
      )
      .finally(() => {
        if (!ignore) {
          setIsLoadingFilters(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  const areaOptions = useMemo(
    () => areasList.map((area) => area.name).filter(Boolean),
    [areasList]
  );

  const selectedAreaModel = useMemo(
    () => areasList.find((area) => area.name === selectedArea),
    [areasList, selectedArea]
  );

  const projectOptions = useMemo(() => {
    const filteredProperties = selectedArea
      ? propertiesList.filter(
          (property) =>
            property.areaName === selectedArea ||
            Boolean(selectedAreaModel?.id && property.areaId === selectedAreaModel.id)
        )
      : propertiesList;
    const propertyNames = filteredProperties
      .map((property) => property.name)
      .filter((propertyName): propertyName is string => Boolean(propertyName));

    return Array.from(new Set(propertyNames));
  }, [propertiesList, selectedArea, selectedAreaModel]);

  const selectedProjectValue = projectOptions.includes(selectedProject) ? selectedProject : '';

  const filteredUnits = useMemo(
    () =>
      units.filter((unit) => unit.area === selectedArea && unit.project === selectedProjectValue),
    [units, selectedArea, selectedProjectValue]
  );

  const selectedUnit = filteredUnits.find((unit) => unit.id === selectedUnitId) || filteredUnits[0];

  const hasActiveFilters = Boolean(selectedArea || selectedProjectValue || selectedUnitShortName);

  const handleApplyFilters = async () => {
    let request: UnitHistoryRequestModel;

    if (selectedUnitShortName) {
      request = { shortName: selectedUnitShortName };
    } else if (selectedProjectValue) {
      const property = propertiesList.find(
        (item) =>
          item.name === selectedProjectValue &&
          (item.areaName === selectedArea ||
            Boolean(selectedAreaModel?.id && item.areaId === selectedAreaModel.id))
      );

      if (!property?.id) {
        setHistoryError('Unable to identify the selected project.');
        return;
      }

      request = { propertyId: property.id };
    } else if (selectedAreaModel?.id) {
      request = { areaId: selectedAreaModel.id };
    } else {
      setHistoryError('Select an area, project, or unit short code to load unit timelines.');
      return;
    }

    setIsLoadingHistory(true);
    setHistoryError(undefined);

    try {
      const response = await getUnitHistory(request);

      if (isErrorInfo(response)) {
        setUnits([]);
        setSelectedUnitId(undefined);
        setHistoryError(
          response.message ||
            response.errorMessage ||
            'Unable to load unit timelines. Please try again.'
        );
        return;
      }

      const histories = Array.isArray(response) ? response : [response];
      if (!histories.length) {
        setUnits([]);
        setSelectedUnitId(undefined);
        setHistoryError('No unit history was found for the selected filter.');
        return;
      }

      const mappedUnits = histories.map((history) =>
        mapUnitHistory(history, selectedArea, selectedProjectValue)
      );
      setUnits(mappedUnits);
      setSelectedUnitId(mappedUnits[0].id);
    } catch {
      setUnits([]);
      setSelectedUnitId(undefined);
      setHistoryError('Unable to load unit timelines. Please try again.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAreaChange = (area: string) => {
    unitsRequestIdRef.current += 1;
    setSelectedArea(area);
    setSelectedProject('');
    setUnitShortNames([]);
    setSelectedUnitShortName('');
    setSelectedUnitId(undefined);
    setUnits([]);
    setUnitsError(undefined);
    setHistoryError(undefined);
    setIsLoadingUnits(false);
  };

  const handleProjectChange = async (project: string) => {
    const requestId = unitsRequestIdRef.current + 1;
    unitsRequestIdRef.current = requestId;

    setSelectedProject(project);
    setUnitShortNames([]);
    setSelectedUnitShortName('');
    setSelectedUnitId(undefined);
    setUnits([]);
    setUnitsError(undefined);
    setHistoryError(undefined);

    if (!project) {
      setIsLoadingUnits(false);
      return;
    }

    const property = propertiesList.find(
      (item) =>
        item.name === project &&
        (item.areaName === selectedArea ||
          Boolean(selectedAreaModel?.id && item.areaId === selectedAreaModel.id))
    );

    if (!property?.id) {
      setUnitsError('Unable to identify the selected project.');
      return;
    }

    setIsLoadingUnits(true);

    try {
      const response: UnitTypeUiModel[] | ErrorInfo = await getPropertyUnitTypes(property.id);
      if (unitsRequestIdRef.current !== requestId) return;

      if (isErrorInfo(response)) {
        setUnitsError(response.message || response.errorMessage || 'Unable to load project units.');
        return;
      }

      const shortNames = response
        .reduce<string[]>(
          (allShortNames, unitType) => [
            ...allShortNames,
            ...(unitType.units || [])
              .map((unit) => unit.shortName)
              .filter((shortName): shortName is string => Boolean(shortName))
          ],
          []
        )
        .filter((shortName, index, values) => values.indexOf(shortName) === index)
        .sort((first, second) => first.localeCompare(second));

      setUnitShortNames(shortNames);
      if (!shortNames.length) {
        setUnitsError('No units were found under the selected project.');
      }
    } catch {
      if (unitsRequestIdRef.current === requestId) {
        setUnitsError('Unable to load project units. Please try again.');
      }
    } finally {
      if (unitsRequestIdRef.current === requestId) {
        setIsLoadingUnits(false);
      }
    }
  };

  const handleUnitChange = (shortName: string) => {
    setSelectedUnitShortName(shortName);
    setSelectedUnitId(undefined);
    setUnits([]);
    setHistoryError(undefined);
  };

  const handleResetFilters = () => {
    unitsRequestIdRef.current += 1;
    setSelectedArea(areaOptions[0] || '');
    setSelectedProject('');
    setUnitShortNames([]);
    setSelectedUnitShortName('');
    setSelectedUnitId(undefined);
    setUnits([]);
    setUnitsError(undefined);
    setHistoryError(undefined);
    setIsLoadingUnits(false);
  };

  const renderSelect = (
    label: string,
    value: string,
    options: string[],
    onChange: (value: string) => void,
    className = 'w-[230px]',
    disabled = false
  ) => {
    const selectOptions = options.map((option) => ({ value: option, label: option }));
    const selectedOption = selectOptions.find((option) => option.value === value) || null;

    return (
      <label className={`block ${className}`}>
        <span className="mb-2 block text-[12px] font-gilroy-bold text-[#696C73]">{label}</span>
        <Select
          value={selectedOption}
          options={selectOptions}
          onChange={(option: ValueType<SelectOption, false>) => onChange(option?.value || '')}
          isDisabled={disabled}
          noOptionsMessage={() => 'No options available'}
          className="text-[15px] font-gilroy-medium"
          styles={
            {
              control: (base: any, state: any) => ({
                ...base,
                minHeight: 44,
                height: 44,
                borderColor: state.isFocused ? '#248158' : '#D9DDE3',
                borderRadius: 8,
                boxShadow: state.isFocused ? '0 0 0 2px rgba(36, 129, 88, 0.1)' : 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#248158' : '#D9DDE3'
                }
              }),
              valueContainer: (base: any) => ({
                ...base,
                height: 44,
                padding: '0 16px'
              }),
              indicatorsContainer: (base: any) => ({
                ...base,
                height: 44
              }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: (base: any) => ({
                ...base,
                color: '#798196',
                paddingRight: 12
              }),
              singleValue: (base: any) => ({
                ...base,
                color: '#2B2D31'
              }),
              menu: (base: any) => ({
                ...base,
                zIndex: 20
              })
            } as any
          }
        />
      </label>
    );
  };

  return (
    <main className="min-h-screen bg-[#FAF8F6] px-6 py-8 font-gilroy-medium text-[#2B2D31] md:px-10">
      <header>
        <h1 className="m-0 font-gilroy-bold text-[30px] leading-[38px] text-[#2B2D31]">
          Unit Life Cycle
        </h1>
        <p className="mb-0 mt-1 text-[16px] leading-6 text-[#73777F]">
          Audit unit status, block dates, publish history, and reservation activity by area and
          project.
        </p>
      </header>

      <section className="mt-6 flex flex-col gap-6 rounded-lg bg-white px-6 py-6 shadow-[0_3px_12px_rgba(17,24,39,0.08)] lg:flex-row lg:items-end">
        {renderSelect(
          'Area',
          selectedArea,
          areaOptions,
          handleAreaChange,
          'w-[230px]',
          isLoadingFilters
        )}
        {renderSelect(
          'Project',
          selectedProjectValue,
          projectOptions,
          handleProjectChange,
          'w-[290px]',
          isLoadingFilters || !projectOptions.length
        )}
        {renderSelect(
          'Unit short code',
          selectedUnitShortName,
          unitShortNames,
          handleUnitChange,
          'w-[300px]',
          isLoadingFilters || isLoadingUnits || !selectedProjectValue || !unitShortNames.length
        )}
        <div className="flex flex-1 justify-end gap-4">
          <button
            type="button"
            className="h-[44px] min-w-[112px] rounded-lg border border-[#B8D3C8] bg-white px-8 text-[14px] font-gilroy-bold text-[#248158] transition hover:bg-[#F4FAF7] focus:outline-none focus:ring-2 focus:ring-[#248158]/20"
            onClick={handleResetFilters}
            disabled={isLoadingUnits || isLoadingHistory}
          >
            Reset
          </button>
          <button
            type="button"
            className="h-[44px] min-w-[150px] rounded-lg bg-[#248158] px-8 text-[14px] font-gilroy-bold text-white transition hover:bg-[#1E704D] focus:outline-none focus:ring-2 focus:ring-[#248158]/20"
            onClick={handleApplyFilters}
          >
            {isLoadingHistory ? 'Loading…' : 'Apply filters'}
          </button>
        </div>
      </section>

      {filtersError && (
        <div className="mt-4 rounded-lg border border-[#F3C6BE] bg-[#FFF7F5] px-4 py-3 text-[14px] font-gilroy-bold text-[#C93625]">
          {filtersError}
        </div>
      )}

      {isLoadingUnits && (
        <div className="mt-4 rounded-lg border border-[#B8D3C8] bg-[#F4FAF7] px-4 py-3 text-[14px] font-gilroy-bold text-[#248158]">
          Loading units under {selectedProjectValue}…
        </div>
      )}

      {unitsError && (
        <div className="mt-4 rounded-lg border border-[#F3C6BE] bg-[#FFF7F5] px-4 py-3 text-[14px] font-gilroy-bold text-[#C93625]">
          {unitsError}
        </div>
      )}

      {historyError && (
        <div className="mt-4 rounded-lg border border-[#F3C6BE] bg-[#FFF7F5] px-4 py-3 text-[14px] font-gilroy-bold text-[#C93625]">
          {historyError}
        </div>
      )}

      <section className="mt-8 grid gap-6 xl:h-[calc(100vh-360px)] xl:min-h-[480px] xl:grid-cols-[454px_1fr]">
        <aside className="flex min-h-0 flex-col rounded-lg bg-white p-6 shadow-[0_3px_12px_rgba(17,24,39,0.08)]">
          <h2 className="m-0 font-gilroy-bold text-[26px] leading-8 text-[#2B2D31]">Units</h2>
          <p className="mb-4 mt-1 text-[15px] leading-6 text-[#73777F]">
            {filteredUnits.length} units found
          </p>
          <div className="h-px bg-[#D9DDE3]" />

          <div className="mt-5 min-h-0 flex-1 space-y-[18px] overflow-y-auto overscroll-contain pr-2">
            {filteredUnits.length ? (
              filteredUnits.map((unit) => {
                const isSelected = selectedUnit?.id === unit.id;

                return (
                  <button
                    key={unit.id}
                    type="button"
                    className={`flex min-h-[88px] w-full items-center justify-between rounded-lg border bg-white px-[18px] py-4 text-left transition hover:border-[#248158] ${
                      isSelected ? 'border-[#248158]' : 'border-[#D9DDE3]'
                    }`}
                    onClick={() => setSelectedUnitId(unit.id)}
                  >
                    <span>
                      <span className="flex items-center gap-8">
                        <strong className="font-gilroy-bold text-[16px] text-[#2B2D31]">
                          {unit.id}
                        </strong>
                        <span className="text-[15px] text-[#55585F]">{unit.name}</span>
                      </span>
                      <span className="mt-3 block text-[13px] text-[#798196]">
                        Area: {unit.area} • Project: {unit.project}
                      </span>
                    </span>
                    <span
                      className={`rounded-full px-6 py-2 text-[12px] font-gilroy-bold ${
                        statusClassName[unit.status]
                      }`}
                    >
                      {unit.status}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-lg border border-dashed border-[#D9DDE3] px-6 py-12 text-center">
                <h3 className="m-0 font-gilroy-bold text-[18px] text-[#2B2D31]">
                  {hasActiveFilters ? 'Unit ready' : 'No unit selected'}
                </h3>
                <p className="mx-auto mb-0 mt-2 max-w-[320px] text-[14px] leading-6 text-[#73777F]">
                  {hasActiveFilters
                    ? 'Click Apply filters to load the selected unit timeline.'
                    : 'Choose a project, then select a unit short code.'}
                </p>
              </div>
            )}
          </div>
        </aside>

        <section className="min-h-0 overflow-y-auto overscroll-contain rounded-lg bg-white p-7 shadow-[0_3px_12px_rgba(17,24,39,0.08)]">
          {selectedUnit ? (
            <>
              <div>
                <p className="mb-2 mt-0 text-[12px] font-gilroy-bold uppercase text-[#248158]">
                  Selected unit
                </p>
                <h2 className="m-0 font-gilroy-bold text-[30px] leading-[38px] text-[#2B2D31]">
                  {selectedUnit.name}
                </h2>
                <p className="mb-0 mt-1 text-[15px] leading-6 text-[#73777F]">
                  {selectedUnit.area} / {selectedUnit.project}
                  {selectedUnit.floor ? ` / ${selectedUnit.floor}` : ''}
                </p>
              </div>

              <div className="my-4 h-px bg-[#D9DDE3]" />

              {/* <div className="grid gap-6 lg:grid-cols-3">
                <InfoCard
                  label="System add"
                  value={selectedUnit.systemAddDate}
                  user={selectedUnit.systemAddUser}
                />
                <InfoCard
                  label="Publish day"
                  value={selectedUnit.publishDate}
                  user={selectedUnit.publishUser}
                />
                <InfoCard
                  label="Last unpublish"
                  value={selectedUnit.lastUnpublishDate}
                  user={selectedUnit.lastUnpublishUser}
                />
              </div> */}

              <div className="mt-8 grid gap-8 xl:grid-cols-[1fr_308px]">
                <div>
                  <h3 className="m-0 font-gilroy-bold text-[24px] leading-8 text-[#2B2D31]">
                    Life cycle timeline
                  </h3>
                  <p className="mb-5 mt-1 text-[15px] leading-6 text-[#73777F]">
                    Each event shows the date, action, and user email responsible for the change.
                  </p>
                  <div className="space-y-0">
                    {selectedUnit.timeline.map((event, index) => (
                      <div
                        key={event.id}
                        className="relative grid grid-cols-[28px_1fr_1fr] gap-3 pb-7 last:pb-0"
                      >
                        {index < selectedUnit.timeline.length - 1 && (
                          <span className="absolute left-[14px] top-5 h-full w-px bg-[#AFD1C4]" />
                        )}
                        <span
                          className={`relative left-3 z-10 mt-1 h-5 w-5 rounded-full ${
                            timelineEventClassName[event.type]
                          }`}
                        />
                        <span>
                          <strong className="block text-[16px] font-gilroy-bold text-[#2B2D31]">
                            {event.title}
                          </strong>
                          <span className="mt-1 block text-[13px] text-[#55585F]">
                            {event.date}
                          </span>
                        </span>
                        <span className="whitespace-pre-line pt-1 text-[14px] leading-6 text-[#55585F]">
                          {event.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* <div className="space-y-5">
                  <ReservationsPreview reservations={selectedUnit.reservations || []} />
                </div> */}
              </div>
            </>
          ) : (
            <div className="flex min-h-[560px] items-center justify-center rounded-lg border border-dashed border-[#D9DDE3] px-6 text-center">
              <div>
                <p className="mb-2 mt-0 text-[12px] font-gilroy-bold uppercase text-[#248158]">
                  Selected unit
                </p>
                <h2 className="m-0 font-gilroy-bold text-[30px] leading-[38px] text-[#2B2D31]">
                  Select a unit to view lifecycle details
                </h2>
                <p className="mx-auto mb-0 mt-2 max-w-[520px] text-[15px] leading-6 text-[#73777F]">
                  Unit status, publish history, block data, and reservations will appear here when
                  data is available.
                </p>
              </div>
            </div>
          )}
        </section>
      </section>
    </main>
  );
};

const ReservationsPreview: React.FC<{ reservations: ReservationItem[] }> = ({ reservations }) => (
  <div className="box-border min-h-[190px] rounded-lg border border-[#E6E8EC] bg-white p-4">
    <div className="flex items-center justify-between border-b border-[#E6E8EC] pb-3">
      <h3 className="m-0 font-gilroy-bold text-[20px] leading-6 text-[#2B2D31]">
        All reservations
      </h3>
      <span className="rounded-full border border-[#B8D3C8] bg-[#E7F2EE] px-4 py-1 text-[12px] font-gilroy-bold text-[#176D4C]">
        Loaded
      </span>
    </div>
    <div className="mt-4 space-y-4">
      {reservations.length ? (
        reservations.map((reservation) => (
          <div
            key={reservation.id}
            className="grid grid-cols-[90px_1fr_auto] items-center gap-2 text-[14px]"
          >
            <strong className="font-gilroy-bold text-[#2B2D31]">{reservation.id}</strong>
            <span className="text-[#55585F]">{reservation.dateRange}</span>
            <span className={reservationStatusClassName[reservation.status]}>
              {reservation.status}
            </span>
          </div>
        ))
      ) : (
        <p className="mb-0 mt-8 text-center text-[14px] text-[#73777F]">
          No reservations available
        </p>
      )}
    </div>
  </div>
);

export default UnitLifeCycleListContainer;
